import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  InputText,
  Select,
  type SelectOption,
  SectionCard,
} from "@/deps/ui-library";
import { useInviteUser, useResendInvitation, useUsers } from "@/api/queries";
import { useInvitationsEnabled } from "@/app/session";
import { useSubscription } from "@/app/subscription";
import { useToast } from "@/app/toasts";
import type { RoleName } from "@/api/types";
import { Dialog } from "@/components/Dialog";
import { EmptyState } from "@/components/EmptyState";
import { formatDate } from "@/lib/dates";
import { ROLE_NAMES } from "@/lib/permissions";
import { DateField } from "@/components/DateField";

const ROLE_OPTIONS: SelectOption[] = ROLE_NAMES.map((name) => ({ value: name, label: name }));

/**
 * Who can get in, and until when.
 *
 * The list was four names in a `const` and the invite dialog's button was
 * `onClick={() => setInviting(false)}` — it closed, and nobody was invited. WP9's criterion is an
 * "invite dialog with access expiry"; the dialog had the expiry field and no way to send anything,
 * and no role field at all, which is the one thing an invitation cannot be sent without.
 */
export function UsersTab() {
  const { data: users, isPending } = useUsers();
  const invite = useInviteUser();
  const resend = useResendInvitation();
  const invitations = useInvitationsEnabled();
  const toast = useToast();

  const [open, setOpen] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<RoleName>("Accountant");
  const [expires, setExpires] = useState("");

  const canSend = fullName.trim() !== "" && email.trim() !== "" && !invite.isPending;

  // The plan's users and those bought (suite ADR-0096). The API refuses past it too.
  const status = useSubscription()?.status;
  const seats =
    status?.enabled && status.includedUsers != null
      ? status.includedUsers + (status.extraUsers ?? 0)
      : null;
  const inUse = (users ?? []).filter((user) => user.status !== "disabled").length;
  const full = seats !== null && users !== undefined && inUse >= seats;

  const close = () => {
    setOpen(false);
    invite.reset();
    setFullName("");
    setEmail("");
    setExpires("");
  };

  return (
    <SectionCard
      title="Users"
      note={seats !== null && users !== undefined ? `${inUse} of ${seats} in use` : undefined}
      actions={
        <Button
          colorScheme="default"
          variant="outline"
          size="sm"
          disabled={full}
          onClick={() => setOpen(true)}
        >
          Add
        </Button>
      }
      flush
    >
      {full ? (
        <p role="status" className="text-fg-muted border-line-soft border-b px-4 py-2.5 text-sm">
          All {seats} users are in use. An administrator can add users to the subscription.
        </p>
      ) : null}
      {isPending ? (
        <p className="text-fg-muted p-4 text-sm">Loading</p>
      ) : (users?.length ?? 0) === 0 ? (
        <EmptyState
          icon="Users"
          title="Nobody yet"
          description="People invited to these books appear here, with the role they hold and when their access ends."
        />
      ) : (
        <ul className="flex flex-col">
          {users!.map((user) => (
            <li
              key={user.id}
              className="border-line-soft flex items-center gap-3 border-b px-4 py-2.5 last:border-0"
            >
              <span className="min-w-0 flex-1">
                <span className="text-fg block text-sm">{user.fullName}</span>
                <span className="text-fg-hint block text-xs">{user.email}</span>
              </span>

              {user.status === "invited" ? (
                <Badge colorScheme="warning" size="sm">
                  Invited
                </Badge>
              ) : null}

              {/* The link in the mail stops working after a while, or the mail goes astray. */}
              {user.status === "invited" && invitations ? (
                <Button
                  colorScheme="default"
                  variant="ghost"
                  size="xs"
                  disabled={resend.isPending}
                  onClick={() =>
                    resend.mutate(user.id, {
                      onSuccess: () =>
                        toast({
                          title: `Invitation sent again to ${user.email}.`,
                          scheme: "success",
                        }),
                      onError: (error) =>
                        toast({
                          title: error instanceof Error ? error.message : "That could not be sent.",
                          scheme: "danger",
                        }),
                    })
                  }
                >
                  Send again
                </Button>
              ) : null}

              <Badge colorScheme="default" size="sm">
                {user.role}
              </Badge>

              {/* An external auditor with an expiry is the case worth showing: access that ends by
                  itself is the difference between a review and a standing key to the books. */}
              {user.accessExpires ? (
                <span className="text-warn-text text-xs">
                  Access ends {formatDate(user.accessExpires)}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <Dialog
        open={open}
        onClose={close}
        title="Add somebody"
        description={
          invitations
            ? "They get an email: an invitation to set a password, or a note if they already have a Rigo ID. Access can be set to end on a date, which is useful for an auditor who needs the books for a fortnight."
            : "They need a Rigo ID already: no invitation email is sent yet. Access can be set to end on a date, which is useful for an auditor who needs the books for a fortnight."
        }
        footer={
          <Button
            colorScheme="primary"
            variant="solid"
            size="sm"
            disabled={!canSend}
            onClick={() =>
              invite.mutate(
                {
                  fullName,
                  email,
                  role,
                  // Blank means access that does not expire, which is different from a date that
                  // has not been chosen yet — so it is sent as null rather than "".
                  accessExpires: expires === "" ? null : expires,
                },
                { onSuccess: close },
              )
            }
          >
            {invite.isPending ? "Adding" : invitations ? "Send invitation" : "Add person"}
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          {invite.isError ? (
            <Alert
              config={{ variant: "subtle", colorScheme: "danger" }}
              description={
                invite.error instanceof Error
                  ? invite.error.message
                  : "That invitation could not be sent."
              }
            />
          ) : null}

          <InputText
            label="Full name"
            required
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
          />

          <InputText
            label="Email"
            required
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />

          <Select
            label="Role"
            required
            options={ROLE_OPTIONS}
            value={role}
            showClearButton={false}
            onChange={(details) => {
              const picked = Array.isArray(details.value) ? details.value[0] : details.value;
              if (picked) setRole(String(picked.value) as RoleName);
            }}
          />

          <DateField label="Access ends" clearable value={expires} onChange={setExpires} />
        </div>
      </Dialog>
    </SectionCard>
  );
}
